import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useAutoHide — controls that come and go (Plan G).
 *
 * `visible` is true after any poke and for `delayMs` after the last one
 * while `active` (playing); while not active, or when `hold` is set (a
 * control has focus, reduced motion), they stay. `poke()` is what a tap on
 * the stage or a control calls. `toggle()` is the stage's own tap: hide if
 * shown, show if hidden.
 */
export function useAutoHide(active, delayMs = 3000, hold = false) {
  const [visible, setVisible] = useState(true);
  const timer = useRef(null);
  const arm = useCallback(() => {
    clearTimeout(timer.current);
    if (!active || hold) return;
    timer.current = setTimeout(() => setVisible(false), delayMs);
  }, [active, hold, delayMs]);
  const poke = useCallback(() => { setVisible(true); arm(); }, [arm]);
  const toggle = useCallback(() => {
    setVisible(v => {
      if (v) { clearTimeout(timer.current); return false; }
      arm(); return true;
    });
  }, [arm]);
  useEffect(() => {
    if (!active || hold) { clearTimeout(timer.current); setVisible(true); return; }
    arm();
    return () => clearTimeout(timer.current);
  }, [active, hold, arm]);
  return { visible, poke, toggle };
}
